import store from "../../store";
import { dropdownOptionsApiSlice, publicDropdownOptionsApiSlice } from "../dropdownOptions/dropdownOptionsApiSlice";
import type { FetchOptionsKit, Option } from "../../../types/modulesInterfaces";
import type { UnknownAction, ThunkAction } from "@reduxjs/toolkit";
import type {
	QueryActionCreatorResult, QueryReturnValue, BaseQueryApi,
	FetchArgs,
	FetchBaseQueryError,
	FetchBaseQueryMeta,
	QueryDefinition,
} from "@reduxjs/toolkit/query";

import { Project } from "../../../types/interfaces";

type PropType = ThunkAction<
	QueryActionCreatorResult<
		QueryDefinition<
			any,
			(
				args: string | FetchArgs,
				api: BaseQueryApi,
				extraOptions: {},
			) => Promise<
				QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>
			>,
			never,
			Option[],
			"api"
		>
	>,
	any,
	any,
	UnknownAction
>;
type OptionName =
	| "climate"
	| "soil"
	| "fishery"
	| "fish"
	| "smallFishery"
	| "largeFishery"
	| "crop"
	| "tillage"
	| "organicInput"
	| "residue"
	| "landUseType"
	| "grassland"
	| "moisture"
	| "projectStatus"
	| "country"
	| "GWPSource"
	| "fundingAgency"
	| "executingAgency"
	| "livestockCategory"
	| "livestockProd"
	| "livestockMngmTypes"
	| "waterMngmTypesBC"
	| "waterMngmTypesAC"
	| "organicAmendmentTypes"
	| "forestTypes"
	| "disturbanceTypes"
	| "forestConditionTypes"
	| "trophicTypes"
	| "waterbodiesTypes"
	| "macroFuelTypes"
	| "fuelTypes"
	| "peatTypes"
	| "fireTypes"
	| "roadTypes"
	| "buildingTypes"
	| "salinity"
	| "emissionFactorSources"
	| "irrigationSystemTypes"
	| "settlementTypes"
	| "changeRates"
	| "refrigerantTypes"
	| "packagingMaterialTypes"
	| "inputTypes"
	| "dataSourceTypes";

interface GetOptionsParams {
	filterId?: number,
	project?: Project,
	module_types?: number,
	climate?: number,
	moisture?: number,
	search?: string[],
	isPublic?: boolean,
}

export const getOptions = async (optionName: OptionName, optParams?: GetOptionsParams) => {
	let thunkOption: PropType | undefined = undefined
	const { filterId, module_types, climate, moisture, search, isPublic } = optParams ?? {}
	const apiSlice = isPublic ? publicDropdownOptionsApiSlice : dropdownOptionsApiSlice;
	switch (optionName) {
		case "climate":
			thunkOption = apiSlice.endpoints.getClimateTypes.initiate();
			break;
		case "moisture":
			thunkOption = apiSlice.endpoints.getMoistureTypes.initiate();
			break;
		case "soil":
			thunkOption = apiSlice.endpoints.getSoilTypes.initiate({ moduleId: filterId })
			break;
		case "fishery":
			thunkOption = apiSlice.endpoints.getTypesOfFishery.initiate()
			break;
		case "fish":
			thunkOption = apiSlice.endpoints.getFishCategories.initiate()
			break;
		case "smallFishery":
			thunkOption = apiSlice.endpoints.getSmallFishGearCategory.initiate({})
			break;
		case "largeFishery":
			thunkOption = apiSlice.endpoints.getLargeFishGearCategories.initiate()
			break;
		case "crop":
			thunkOption = apiSlice.endpoints.getCropTypes.initiate() /* TODO: REMOVE */
			break;
		case "tillage":
			thunkOption = apiSlice.endpoints.getTillageTypes.initiate()
			break;
		case "organicInput":
			thunkOption = apiSlice.endpoints.getOrganicInputTypes.initiate()
			break;
		case "residue":
			thunkOption = apiSlice.endpoints.getResidueTypes.initiate()
			break;
		case "grassland":
			thunkOption = apiSlice.endpoints.getGrasslandTypes.initiate()
			break;
		case "projectStatus":
			thunkOption = apiSlice.endpoints.getProjectStatuses.initiate()
			break;
		case "country":
			thunkOption = apiSlice.endpoints.getCountries.initiate()
			break;
		case "GWPSource":
			thunkOption = apiSlice.endpoints.getGWPSources.initiate()
			break;
		case "executingAgency":
			thunkOption = apiSlice.endpoints.getExecutingAgencies.initiate()
			break;
		case "fundingAgency":
			thunkOption = apiSlice.endpoints.getFundingAgencies.initiate()
			break;
		case "livestockCategory":
			thunkOption = apiSlice.endpoints.getLivestockCategories.initiate()
			break;
		case "livestockProd":
			thunkOption = apiSlice.endpoints.getLivestockProductionTypes.initiate()
			break;
		case "livestockMngmTypes":
			thunkOption = apiSlice.endpoints.getManureManagementTypes.initiate()
			break;
		case "waterMngmTypesBC":
			thunkOption = apiSlice.endpoints.getWaterManagementTypesBC.initiate()
			break;
		case "waterMngmTypesAC":
			thunkOption = apiSlice.endpoints.getWaterManagementTypesAC.initiate()
			break;
		case "organicAmendmentTypes":
			thunkOption = apiSlice.endpoints.getOrganicAmendmentTypes.initiate()
			break;
		case "disturbanceTypes":
			thunkOption = apiSlice.endpoints.getDisturbanceTypes.initiate()
			break;
		case "forestTypes":
			thunkOption = apiSlice.endpoints.getForestTypes.initiate()
			break;
		case "fuelTypes":
			thunkOption = apiSlice.endpoints.getFuelTypes.initiate({ search, module_types })
			break;
		case "macroFuelTypes":
			thunkOption = apiSlice.endpoints.getMacroFuelTypes.initiate()
			break;
		case "forestConditionTypes":
			thunkOption = apiSlice.endpoints.getForestConditionTypes.initiate()
			break;
		case "trophicTypes":
			thunkOption = apiSlice.endpoints.getTrophicTypes.initiate()
			break;
		case "waterbodiesTypes":
			thunkOption = apiSlice.endpoints.getWaterbodyTypes.initiate()
			break;
		case "roadTypes":
			thunkOption = apiSlice.endpoints.getRoadTypes.initiate()
			break;
		case "buildingTypes":
			thunkOption = apiSlice.endpoints.getBuildingTypes.initiate()
			break;
		case "irrigationSystemTypes":
			thunkOption = apiSlice.endpoints.getIrrigationSystemTypes.initiate({
				moduleTypes: module_types
			})
			break;
		case "peatTypes":
			thunkOption = apiSlice.endpoints.getPeatTypes.initiate()
			break;
		case "fireTypes":
			thunkOption = apiSlice.endpoints.getFireTypes.initiate()
			break;
		case "salinity":
			thunkOption = apiSlice.endpoints.getSalinityTypes.initiate()
			break;
		case "emissionFactorSources":
			thunkOption = apiSlice.endpoints.getEmissionFactorSources.initiate()
			break;
		case "refrigerantTypes":
			thunkOption = apiSlice.endpoints.getRefrigerantTypes.initiate()
			break;
		case "packagingMaterialTypes":
			thunkOption = apiSlice.endpoints.getPackagingMaterialTypes.initiate()
			break;
		case "settlementTypes":
			thunkOption = apiSlice.endpoints.getSettlementTypes.initiate()
			break;
		case "changeRates":
			thunkOption = apiSlice.endpoints.getChangeRates.initiate()
			break;
		case "inputTypes":
			thunkOption = apiSlice.endpoints.getInputTypes.initiate()
			break;
		case "dataSourceTypes":
			thunkOption = dropdownOptionsApiSlice.endpoints.getDataSourceTypes.initiate(filterId)
			break;
		case "landUseType": {
			if (filterId && climate && moisture) {
				thunkOption = apiSlice.endpoints.getLandUseTypes.initiate({ moduleType: filterId, climate, moisture })
			}
			else thunkOption = apiSlice.endpoints.getLandUseTypes.initiate({})
		}
	}
	if (!thunkOption) return { data: [] as Option[], isLoading: false, error: {} }
	const options = await fetchOptions(thunkOption);
	return options
}

const fetchOptions = async (
	thunkAction: PropType,
): Promise<FetchOptionsKit> => {
	try {
		const result = store.dispatch(thunkAction);
		const { data, error, isLoading } = await result;
		if (error)
			console.error("an error occurred while fetching options", error);

		return { data: data ? data : ([] as Option[]), error, isLoading };
	} catch (error) {
		console.log(error);
	}
	return { data: [] as Option[], isLoading: false, error: {} };
};
